/**
 * Image Class
 *
 * Represents an image/graphic element in the puzzle.
 * Migrated from the legacy Kep constructor function.
 */

/**
 * Image configuration interface
 */
export interface ImageConfig {
    id: number;
    source: string;
    top: number;
    left: number;
    width: number;
    height: number;
}

/**
 * Image class for graphic elements in the puzzle.
 */
export class Image {
    private readonly _id: number;
    private readonly _elementId: string;
    private _source: string;
    private _top: number;
    private _left: number;
    private _width: number;
    private _height: number;

    constructor(config: ImageConfig) {
        this._id = config.id;
        this._elementId = `kep-${config.id}`;
        this._source = config.source;
        this._top = config.top;
        this._left = config.left;
        this._width = config.width;
        this._height = config.height;
    }

    // ============================================================================
    // Getters
    // ============================================================================

    get id(): number {
        return this._id;
    }

    get elementId(): string {
        return this._elementId;
    }

    get source(): string {
        return this._source;
    }

    get top(): number {
        return this._top;
    }

    get left(): number {
        return this._left;
    }

    get width(): number {
        return this._width;
    }

    get height(): number {
        return this._height;
    }

    // ============================================================================
    // Setters
    // ============================================================================

    set source(value: string) {
        this._source = value;
    }

    set top(value: number) {
        this._top = value;
    }

    set left(value: number) {
        this._left = value;
    }

    set width(value: number) {
        this._width = value;
    }

    set height(value: number) {
        this._height = value;
    }

    // ============================================================================
    // Render to HTML
    // ============================================================================

    toHtml(): string {
        const style = [
            `top:${this._top}px`,
            `left:${this._left}px`,
            `width:${this._width}px`,
            `height:${this._height}px`,
            `background-image:url(${this._source})`,
        ].join('; ');

        return `<div id="${this._elementId}" class="kép" style="${style}"></div>`;
    }

    // ============================================================================
    // Serialization
    // ============================================================================

    toXml(): string {
        return `<kép forrás="${this._source}" felső="${this._top}" bal="${this._left}" szélesség="${this._width}" magasság="${this._height}"/>`;
    }

    static fromXml(element: Element, id: number): Image {
        return new Image({
            id,
            source: element.getAttribute('forrás') ?? '',
            top: parseInt(element.getAttribute('felső') ?? '0', 10),
            left: parseInt(element.getAttribute('bal') ?? '0', 10),
            width: parseInt(element.getAttribute('szélesség') ?? '0', 10),
            height: parseInt(element.getAttribute('magasság') ?? '0', 10),
        });
    }
}
